"use client";

import * as React from "react";
import { 
	Typography, 
	Menu, 
	MenuItem, 
	ListItemText, 
	CircularProgress,
	Button
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { ListIcon } from "@phosphor-icons/react/dist/ssr/List";
import { CaretDownIcon } from "@phosphor-icons/react/dist/ssr/CaretDown";

import type { NavItemConfig } from "@/types/nav";
import { usePopover } from "@/hooks/use-popover";
import { useAuth } from "@/components/core/auth-provider";
import { useAccountsList, useImpersonateAccount, Account } from "@/hooks/api/use-accounts";

import { MobileNav } from "../mobile-nav";
import { UserPopover } from "../user-popover/user-popover";
import { toast } from "@/components/core/toaster";

export interface MainNavProps {
	items: NavItemConfig[];
}

export function MainNav({ items }: MainNavProps): React.JSX.Element {
	const [openNav, setOpenNav] = React.useState<boolean>(false);
	const { user, account, setAccount, getMe } = useAuth();
	
	// Account selection menu
	const [accountMenuAnchor, setAccountMenuAnchor] = React.useState<null | HTMLElement>(null);
	const [impersonateId, setImpersonateId] = React.useState<string | null>(null);
	
	const { data: accountsData } = useAccountsList(undefined, !user?.isSuperAdmin);
	const accounts = accountsData || [];
	const impersonateAccountMutation = useImpersonateAccount();
	
	const isAccountMenuOpen = Boolean(accountMenuAnchor);
	
	const handleAccountMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		setAccountMenuAnchor(event.currentTarget);
	};
	
	const handleAccountMenuClose = () => {
		setAccountMenuAnchor(null);
	};
	
	const handleImpersonate = async (selectedAccount: Account) => {
		try {
			setImpersonateId(selectedAccount.id);
			const response = await impersonateAccountMutation.mutateAsync(selectedAccount.id);
			setAccount(response.account);
			await getMe();
			toast.success('Account selected successfully!');
			handleAccountMenuClose();
		} catch (error) {
			console.error("Failed to impersonate account:", error);
		} finally {
			setImpersonateId(null);
		}
	};
	return (
		<React.Fragment>
			<Box
				component="header"
				sx={{
					"--MainNav-background": "var(--mui-palette-background-default)",
					"--MainNav-divider": "var(--mui-palette-divider)",
					bgcolor: "var(--MainNav-background)",
					left: 0,
					position: "sticky",
					pt: { lg: "var(--Layout-gap)" },
					top: 0,
					width: "100%",
					zIndex: "var(--MainNav-zIndex)",
				}}
			>
				<Box
					sx={{
						borderBottom: "1px solid var(--MainNav-divider)",
						display: "flex",
						flex: "1 1 auto",
						minHeight: "var(--MainNav-height)",
						px: { xs: 2, lg: 3 },
						py: 1,
					}}
				>
					<Stack direction="row" spacing={2} sx={{ alignItems: "center", flex: "1 1 auto" }}>
						<IconButton
							onClick={(): void => {
								setOpenNav(true);
							}}
							sx={{ display: { lg: "none" } }}
						>
							<ListIcon />
						</IconButton>
						{/* <SearchButton /> */}
					</Stack>
					<Stack
						direction="row"
						spacing={2}
						sx={{ alignItems: "center", flex: "1 1 auto", justifyContent: "flex-end" }}
					>
						{/* <NotificationsButton /> */}
						{/* <ContactsButton /> */}
						{user?.isSuperAdmin === false ? (
							<Button
								variant="text"
								onClick={handleAccountMenuClick}
								endIcon={<CaretDownIcon />}
								sx={{ 
									color: "text.secondary", 
									textTransform: "none",
									display: { xs: "none", lg: "flex" }
								}}
							>
								{account ? `${account?.name}` : `Welcome back, ${user?.name}`}
							</Button>
						) : (
							<Typography color="text.secondary" variant="body2" sx={{ display: { xs: "none", lg: "block" } }}>
								{account ? `Currently impersonating - ${account?.name}` : `Welcome back, ${user?.name}`}
							</Typography>
						)}
						<Divider
							flexItem
							orientation="vertical"
							sx={{ borderColor: "var(--MainNav-divider)", display: { xs: "none", lg: "block" } }}
						/>
						{/* <LanguageSwitch /> */}
						<UserButton />
					</Stack>
				</Box>
			</Box>
			<MobileNav
				items={items}
				onClose={() => {
					setOpenNav(false);
				}}
				open={openNav}
			/>
			
			{/* Account Selection Menu */}
			<Menu
				anchorEl={accountMenuAnchor}
				open={isAccountMenuOpen}
				onClose={handleAccountMenuClose}
				PaperProps={{
					sx: {
						maxHeight: 300,
						width: 300,
					},
				}}
			>
				{accounts.map((accountItem) => (
					<MenuItem
						key={accountItem.id}
						onClick={() => handleImpersonate(accountItem)}
						disabled={impersonateId === accountItem.id || account?.id === accountItem.id}
            selected={account?.id === accountItem.id}
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
              fontSize: "15px",
						}}
					>
						<ListItemText
							primary={accountItem.name}
              primaryTypographyProps={{
                fontSize: "15px",
              }}
						/>
						{impersonateId === accountItem.id && (
							<CircularProgress size={16} />
						)}
					</MenuItem>
				))}
			</Menu>
		</React.Fragment>
	);
}






function UserButton(): React.JSX.Element {
	const popover = usePopover<HTMLButtonElement>();

	return (
		<React.Fragment>
			<Box
				component="button"
				onClick={popover.handleOpen}
				ref={popover.anchorRef}
				sx={{ border: "none", background: "transparent", cursor: "pointer", p: 0 }}
			>
				<Badge
					anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
					color="success"
					sx={{
						"& .MuiBadge-dot": {
							border: "2px solid var(--MainNav-background)",
							borderRadius: "50%",
							bottom: "6px",
							height: "12px",
							right: "6px",
							width: "12px",
						},
					}}
					variant="dot"
				>
					<Avatar sx={{ backgroundColor: "var(--mui-palette-primary-main)" }} />
				</Badge>
			</Box>
			<UserPopover anchorEl={popover.anchorRef.current} onClose={popover.handleClose} open={popover.open} />
		</React.Fragment>
	);
}
